import { ComponentPortal, DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Injectable, NgModuleRef, TemplateRef, Type, ViewContainerRef, inject } from '@angular/core';

import { ToastMessagesService } from '@hdms/shared/ui-core';
import { EnvironmentService, FrontendMode, HostContext, PlatformService } from '@hdms/shared/util-core';

import { APP_CONTENT_SELECTOR, APP_FOOTER_SELECTOR, HDMS_MAIN_SELECTOR } from '../constants';

@Injectable()
export class AppPortalService {
  private readonly document = inject(DOCUMENT);
  private readonly platform = inject(PlatformService);
  private readonly env = inject(EnvironmentService);
  private readonly toastMessagesService = inject(ToastMessagesService);
  private readonly applicationRef = inject(ApplicationRef);

  private readonly contentContainer = this.getContentContainer();
  private readonly footerContainer = this.getFooterContainer();

  /**
   * If app runs in Hybris host context with Angular header enabled,
   * create a DOM portal outlet for `website-content` tag, if present.
   * The portal outlet will hold primary router outlet plus related
   * feature route contents.
   * @private
   */
  public renderContentPortal(templateRef: TemplateRef<unknown>, viewContainerRef: ViewContainerRef): void {
    if (this.contentContainer) {
      const portalOutlet = new DomPortalOutlet(this.contentContainer);
      const contentPortal = new TemplatePortal(templateRef, viewContainerRef);
      portalOutlet.attachTemplatePortal(contentPortal);
    }
  }

  /**
   * Render a footer portal outlet attached to `website-footer` tag, if present.
   * Additionally, remove any existing SSR markup, as it has to be recreated by the app
   * once footer will be projected into the portal.
   * @param footer
   * @param ngModuleRef
   * @private
   */
  public renderFooterPortal(footer: Type<unknown>, ngModuleRef: NgModuleRef<unknown>): void {
    if (this.footerContainer) {
      while (this.footerContainer.firstChild) {
        this.footerContainer.removeChild(this.footerContainer.firstChild);
      }
      const portalOutlet = new DomPortalOutlet(
        this.footerContainer,
        ngModuleRef.componentFactoryResolver,
        this.applicationRef
      );
      const footerPortal = new ComponentPortal(footer);
      portalOutlet.attachComponentPortal(footerPortal);
    }
  }

  public configureToastContainer(containerRef: ViewContainerRef): void {
    // in legacy or mixed mode set global toast container to <main/>
    if (this.env.hostContext && this.env.frontendMode !== FrontendMode.FULL) {
      this.toastMessagesService.globalToastContainerElement = this.document.querySelector(HDMS_MAIN_SELECTOR);
    } else {
      this.toastMessagesService.globalToastContainerRef = containerRef;
    }
  }

  private getContentContainer(): HTMLElement {
    return this.platform.isBrowser && this.env.hostContext === HostContext.HYBRIS
      ? this.document.querySelector<HTMLElement>(APP_CONTENT_SELECTOR)
      : null;
  }

  private getFooterContainer(): HTMLElement {
    return this.platform.isBrowser && this.env.hostContext
      ? this.document.querySelector<HTMLElement>(APP_FOOTER_SELECTOR)
      : null;
  }
}
